/**
 * responseBinaryData.js
 * ======================
 * Response tree for the 2-block interaction mode.
 * Parsed from eye2voice-ui/data/responses_binary.csv.
 *
 * Each node: { id, parentId, label, isLeaf, position }
 * Position is always 'left' or 'right' — no 'top' or 'bottom'.
 * The root node (id=1, label='Home') is a container not shown in the UI;
 * its children (id=2, id=3) are the two home-screen choices.
 *
 * getBinaryRootNodes() — returns the two top-level choices (children of Home)
 * getBinaryChildren(parentId) — returns left + right children of a node
 * getBinaryNode(id) — look up a node by id
 */

const nodes = [
  // Root container — not rendered directly
  { id: 1,  parentId: null, label: 'Home',                  isLeaf: false, position: null   },

  // Children of Home (1)
  { id: 2,  parentId: 1,    label: 'Respond',                isLeaf: false, position: 'left'  },
  { id: 3,  parentId: 1,    label: 'Need Something',         isLeaf: false, position: 'right' },

  // Children of Respond (2)
  { id: 4,  parentId: 2,    label: 'Yes',                    isLeaf: true,  position: 'left'  },
  { id: 5,  parentId: 2,    label: 'No',                     isLeaf: true,  position: 'right' },

  // Children of Need Something (3)
  { id: 6,  parentId: 3,    label: 'Food / Water',           isLeaf: false, position: 'left'  },
  { id: 7,  parentId: 3,    label: 'Discomfort / Pain',      isLeaf: false, position: 'right' },

  // Children of Food / Water (6)
  { id: 8,  parentId: 6,    label: 'Food',                   isLeaf: true,  position: 'left'  },
  { id: 9,  parentId: 6,    label: 'Water',                  isLeaf: true,  position: 'right' },

  // Children of Discomfort / Pain (7)
  { id: 10, parentId: 7,    label: 'Discomfort',             isLeaf: false, position: 'left'  },
  { id: 11, parentId: 7,    label: 'Pain',                   isLeaf: false, position: 'right' },

  // Children of Discomfort (10)
  { id: 12, parentId: 10,   label: 'Get Someone',            isLeaf: false, position: 'left'  },
  { id: 13, parentId: 10,   label: 'Need Something',         isLeaf: false, position: 'right' },

  // Children of Pain (11)
  { id: 20, parentId: 11,   label: 'Physical',               isLeaf: false, position: 'left'  },
  { id: 21, parentId: 11,   label: 'Emotional',              isLeaf: false, position: 'right' },

  // Children of Get Someone (12)
  { id: 14, parentId: 12,   label: 'Friend / Family',        isLeaf: true,  position: 'left'  },
  { id: 15, parentId: 12,   label: 'Doctor / Nurse',         isLeaf: true,  position: 'right' },

  // Children of Need Something / Discomfort (13)
  { id: 16, parentId: 13,   label: 'Restroom',               isLeaf: true,  position: 'left'  },
  { id: 17, parentId: 13,   label: 'Object',                 isLeaf: false, position: 'right' },

  // Children of Object (17)
  { id: 18, parentId: 17,   label: 'Get Phone / Device',     isLeaf: true,  position: 'left'  },
  { id: 19, parentId: 17,   label: 'Turn on TV',             isLeaf: true,  position: 'right' },

  // Children of Physical (20)
  { id: 22, parentId: 20,   label: 'Upper Body',             isLeaf: false, position: 'left'  },
  { id: 23, parentId: 20,   label: 'Lower Body',             isLeaf: false, position: 'right' },

  // Children of Emotional (21)
  { id: 54, parentId: 21,   label: 'Not Well',               isLeaf: false, position: 'left'  },
  { id: 55, parentId: 21,   label: 'Uncomfortable',          isLeaf: false, position: 'right' },

  // Children of Upper Body (22)
  { id: 26, parentId: 22,   label: 'Mild / Moderate',        isLeaf: false, position: 'left'  },
  { id: 27, parentId: 22,   label: 'Severe / Emergency',     isLeaf: false, position: 'right' },

  // Children of Lower Body (23)
  { id: 32, parentId: 23,   label: 'Stomach',                isLeaf: false, position: 'left'  },
  { id: 33, parentId: 23,   label: 'Legs',                   isLeaf: false, position: 'right' },

  // Children of Not Well / Emotional (54)
  { id: 56, parentId: 54,   label: 'Tired / Frustrated',     isLeaf: false, position: 'left'  },
  { id: 57, parentId: 54,   label: 'Dizzy / Nauseated',      isLeaf: false, position: 'right' },

  // Children of Uncomfortable / Emotional (55)
  { id: 62, parentId: 55,   label: 'Position Uncomfortable', isLeaf: false, position: 'left'  },
  { id: 63, parentId: 55,   label: 'Too Bright',             isLeaf: false, position: 'right' },

  // Children of Mild / Moderate - Upper (26)
  { id: 28, parentId: 26,   label: 'Mild',                   isLeaf: true,  position: 'left'  },
  { id: 29, parentId: 26,   label: 'Moderate',               isLeaf: true,  position: 'right' },

  // Children of Severe / Emergency - Upper (27)
  { id: 30, parentId: 27,   label: 'Severe',                 isLeaf: true,  position: 'left'  },
  { id: 31, parentId: 27,   label: 'Emergency',              isLeaf: true,  position: 'right' },

  // Children of Stomach (32)
  { id: 34, parentId: 32,   label: 'Mild / Moderate',        isLeaf: false, position: 'left'  },
  { id: 35, parentId: 32,   label: 'Severe / Emergency',     isLeaf: false, position: 'right' },

  // Children of Legs (33)
  { id: 40, parentId: 33,   label: 'Left Leg',               isLeaf: false, position: 'left'  },
  { id: 41, parentId: 33,   label: 'Right Leg',              isLeaf: false, position: 'right' },

  // Children of Tired / Frustrated (56)
  { id: 58, parentId: 56,   label: 'Tired',                  isLeaf: true,  position: 'left'  },
  { id: 59, parentId: 56,   label: 'Frustrated',             isLeaf: true,  position: 'right' },

  // Children of Dizzy / Nauseated (57)
  { id: 60, parentId: 57,   label: 'Dizzy',                  isLeaf: true,  position: 'left'  },
  { id: 61, parentId: 57,   label: 'Nauseated',              isLeaf: true,  position: 'right' },

  // Children of Position Uncomfortable (62)
  { id: 64, parentId: 62,   label: 'Adjust Position',        isLeaf: true,  position: 'left'  },
  { id: 65, parentId: 62,   label: 'Generally Uncomfortable',isLeaf: true,  position: 'right' },

  // Children of Too Bright (63)
  { id: 66, parentId: 63,   label: 'Too Bright',             isLeaf: true,  position: 'left'  },
  { id: 67, parentId: 63,   label: 'Need Shade / Blinds',    isLeaf: true,  position: 'right' },

  // Children of Mild / Moderate - Stomach (34)
  { id: 36, parentId: 34,   label: 'Mild',                   isLeaf: true,  position: 'left'  },
  { id: 37, parentId: 34,   label: 'Moderate',               isLeaf: true,  position: 'right' },

  // Children of Severe / Emergency - Stomach (35)
  { id: 38, parentId: 35,   label: 'Severe',                 isLeaf: true,  position: 'left'  },
  { id: 39, parentId: 35,   label: 'Emergency',              isLeaf: true,  position: 'right' },

  // Children of Left Leg (40)
  { id: 42, parentId: 40,   label: 'Mild / Moderate',        isLeaf: false, position: 'left'  },
  { id: 43, parentId: 40,   label: 'Severe / Emergency',     isLeaf: false, position: 'right' },

  // Children of Right Leg (41)
  { id: 48, parentId: 41,   label: 'Mild / Moderate',        isLeaf: false, position: 'left'  },
  { id: 49, parentId: 41,   label: 'Severe / Emergency',     isLeaf: false, position: 'right' },

  // Children of Mild / Moderate - Left Leg (42)
  { id: 44, parentId: 42,   label: 'Mild',                   isLeaf: true,  position: 'left'  },
  { id: 45, parentId: 42,   label: 'Moderate',               isLeaf: true,  position: 'right' },

  // Children of Severe / Emergency - Left Leg (43)
  { id: 46, parentId: 43,   label: 'Severe',                 isLeaf: true,  position: 'left'  },
  { id: 47, parentId: 43,   label: 'Emergency',              isLeaf: true,  position: 'right' },

  // Children of Mild / Moderate - Right Leg (48)
  { id: 50, parentId: 48,   label: 'Mild',                   isLeaf: true,  position: 'left'  },
  { id: 51, parentId: 48,   label: 'Moderate',               isLeaf: true,  position: 'right' },

  // Children of Severe / Emergency - Right Leg (49)
  { id: 52, parentId: 49,   label: 'Severe',                 isLeaf: true,  position: 'left'  },
  { id: 53, parentId: 49,   label: 'Emergency',              isLeaf: true,  position: 'right' },
];

const nodeMap = new Map(nodes.map(n => [n.id, n]));

/**
 * Returns the two top-level choice nodes (children of the root Home node).
 * Sorted left first, right second — matches the binary layout order.
 */
export function getBinaryRootNodes() {
  return nodes
    .filter(n => n.parentId === 1)
    .sort((a, b) => (a.position === 'left' ? -1 : 1));
}

/**
 * Returns the left and right children of a node, sorted left first.
 * Binary pages always have exactly two children.
 */
export function getBinaryChildren(parentId) {
  return nodes
    .filter(n => n.parentId === parentId)
    .sort((a, b) => (a.position === 'left' ? -1 : 1));
}

/**
 * Look up a single node by id.
 */
export function getBinaryNode(id) {
  return nodeMap.get(id) || null;
}
